import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { dealTokens } from './designTokens';
import SkeletonLoader from './SkeletonLoader';

// Time picker of the Deal design system — the reservation "time" box: the slots grouped
// into Morning / Afternoon / Evening tabs, three buttons per row, the picked one filled.
// `slots` are `{ value, label }` with `label` as "HH:mm" (the hour decides the group);
// `value` is whatever the caller stores. Only groups that have slots get a tab.
const PERIODS = ['morning', 'afternoon', 'evening'];
const PERIOD_FALLBACK = { morning: 'Mattina', afternoon: 'Pomeriggio', evening: 'Sera' };
const periodOf = label => {
  const hour = Number(String(label).split(':')[0]);
  return hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
};

// `fill`: grow to the height left in a flex-column parent (e.g. beside a calendar); the slot
// list then fills it and scrolls inside instead of stopping at 250px.
const SystemTimeSlotPicker = ({ title, slots = [], value, onChange, loading = false, emptyText, fill = false }) => {
  const { t } = useTranslation();
  const groups = PERIODS.reduce((acc, period) => ({ ...acc, [period]: slots.filter(slot => periodOf(slot.label) === period) }), {});
  const periods = PERIODS.filter(period => groups[period].length);
  const selected = slots.find(slot => slot.value === value);
  const [period, setPeriod] = useState(() => (selected ? periodOf(selected.label) : periods[0] || 'morning'));

  // Keep the open tab on a group that exists (slots change with the date / staff).
  useEffect(() => {
    if (!loading && !groups[period]?.length && periods.length) setPeriod(periods[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, slots]);

  return (
    <div
      style={{
        boxSizing: 'border-box',
        ...(fill ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : {}),
        minWidth: 0,
        border: `1px solid ${dealTokens.color.border}`,
        borderRadius: dealTokens.radius.lg,
        padding: dealTokens.space[3],
      }}
    >
      {title && (
        <div style={{ color: dealTokens.color.textMuted, fontSize: 12, fontWeight: dealTokens.font.weightMedium, marginBottom: 8 }}>{title}</div>
      )}
      {!loading && !!periods.length && (
        <div
          style={{
            display: 'flex',
            gap: 4,
            padding: 3,
            marginBottom: dealTokens.space[3],
            background: dealTokens.color.canvas,
            borderRadius: dealTokens.radius.md,
          }}
        >
          {periods.map(key => (
            <button
              key={key}
              type="button"
              aria-pressed={period === key}
              onClick={() => setPeriod(key)}
              style={{
                // Sized by its label (Pomeriggio is the long one); the ellipsis stays for very narrow screens.
                flex: '1 1 auto',
                minWidth: 0,
                height: 30,
                padding: '0 8px',
                border: 0,
                borderRadius: dealTokens.radius.sm,
                background: period === key ? dealTokens.color.surface : 'transparent',
                color: period === key ? dealTokens.color.primary : dealTokens.color.textMuted,
                boxShadow: period === key ? dealTokens.shadow.subtle : 'none',
                fontFamily: dealTokens.font.family,
                fontSize: 11,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
              }}
            >
              {t(`RESERVATIONS.${key}`, PERIOD_FALLBACK[key])}
            </button>
          ))}
        </div>
      )}
      {loading ? (
        <SkeletonLoader type="timeSlots" />
      ) : slots.length ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gridAutoRows: 'min-content',
            alignContent: 'start',
            gap: dealTokens.space[2],
            ...(fill ? { flex: 1, minHeight: 0, maxHeight: 'none' } : { maxHeight: 250 }),
            overflowY: 'auto',
          }}
        >
          {(groups[period] || []).map(slot => {
            const isSelected = slot.value === value;
            return (
              <button
                key={slot.value}
                type="button"
                aria-pressed={isSelected}
                onClick={() => onChange(slot.value)}
                style={{
                  height: 36,
                  minWidth: 0,
                  padding: '0 8px',
                  border: `1px solid ${isSelected ? dealTokens.color.primary : dealTokens.color.border}`,
                  borderRadius: dealTokens.radius.md,
                  background: isSelected ? dealTokens.color.primary : dealTokens.color.surface,
                  color: isSelected ? dealTokens.color.surface : dealTokens.color.text,
                  fontFamily: dealTokens.font.family,
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                {slot.label}
              </button>
            );
          })}
        </div>
      ) : (
        <span style={{ color: dealTokens.color.textMuted, fontSize: 13 }}>{emptyText}</span>
      )}
    </div>
  );
};

export default SystemTimeSlotPicker;
